export interface ParsedReportResponse {
  operations: any[];
  lastProcessedAt: string;
  minProcessedAt: string;
  /** Server hint: next "30 1/4" cron tick after now+4m30s (Y-m-d H:i:s, same convention as last_processed_at) */
  recommendedNextFetchAt: string;
  error: string;
}

/** When the hint is missing or in the past, poll again after this many ms. */
export const REPORT_REFRESH_FALLBACK_MS = 4 * 60 * 1000;

/**
 * Delay until the next report poll. If recommended time is missing or not after `nowMs`, uses {@link REPORT_REFRESH_FALLBACK_MS}.
 */
export function delayMsUntilNextReportFetch(
  recommendedNextFetchAt: string | null | undefined,
  nowMs: number = Date.now(),
): number {
  if (!recommendedNextFetchAt || !String(recommendedNextFetchAt).trim()) {
    return REPORT_REFRESH_FALLBACK_MS;
  }
  const target = new Date(`${String(recommendedNextFetchAt).trim().replace(' ', 'T')}Z`).getTime();
  if (Number.isNaN(target)) {
    return REPORT_REFRESH_FALLBACK_MS;
  }
  if (target > nowMs) {
    return target - nowMs;
  }
  return REPORT_REFRESH_FALLBACK_MS;
}

/**
 * Normalizes report endpoint response for both buffered and streaming modes.
 * Supports legacy payload variants to keep frontend compatibility stable.
 */
export function parseReportResponse(response: any): ParsedReportResponse {
  const payload = response?.data;
  const legacyArray = Array.isArray(payload) ? payload : null;
  const operations = Array.isArray(payload?.operations)
    ? payload.operations
    : (legacyArray || []);

  const lastProcessedAt = payload?.last_processed_at || '';
  const minProcessedAt = payload?.min_processed_at || '';
  const recommendedNextFetchAt = payload?.recommended_next_fetch_at || '';
  const error = payload?.error || response?.error || '';

  return {
    operations,
    lastProcessedAt,
    minProcessedAt,
    recommendedNextFetchAt,
    error,
  };
}
